import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MessageSquare,
  ArrowLeft,
  Send,
  Search,
  Plus,
  MoreVertical,
  Phone,
  Video,
  Paperclip,
  Smile,
  Check,
  CheckCheck,
  Clock,
  User,
  Star,
  Settings,
  X,
  Image as ImageIcon,
  File,
  Video as VideoIcon,
  Edit2,
  Trash2,
  Save,
  MessageCircle,
  FileText
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useMessages } from '../contexts/MessagesContext';
import { api } from '../services/api';
import toast from 'react-hot-toast';

const MessagesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [connectingToSupport, setConnectingToSupport] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedTab, setSelectedTab] = useState(() => {
    return localStorage.getItem('unified-messages-selected-tab') || 'contact-support';
  });
  const [unreadCounts, setUnreadCounts] = useState({});
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingMessageContent, setEditingMessageContent] = useState('');
  const [channelFilter, setChannelFilter] = useState('ALL'); // ALL, WEB, WHATSAPP, SUPPORT
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateVariables, setTemplateVariables] = useState({});
  const [sendingTemplate, setSendingTemplate] = useState(false);

  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const templateSelectorRef = useRef(null);

  // Common emojis for quick access
  const commonEmojis = [
    '😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂',
    '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛',
    '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🥳', '😏', '😒',
    '😞', '😔', '😟', '😕', '🙁', '☹️', '😣', '😖', '😫', '😩',
    '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵',
    '👍', '👎', '👌', '✌️', '🤞', '🤝', '👏', '🙌', '🙏', '💪',
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🤎', '🖤', '🤍', '💔',
    '⭐', '✨', '💫', '🔥', '💯', '✅', '❌', '⚠️', '📝', '📌'
  ];

  // Handle emoji click
  const handleEmojiClick = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Load WhatsApp templates
  const loadTemplates = async () => {
    try {
      const response = await api.get('/whatsapp/templates');
      setTemplates(response.data?.templates || response.templates || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
      toast.error('Failed to load WhatsApp templates');
    }
  };

  // Handle template selection
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    // Extract variables from template body
    const variableMatches = template.components?.[0]?.text?.match(/\{\{(\d+)\}\}/g) || [];
    const variables = {};
    variableMatches.forEach((match, index) => {
      const varNum = match.replace(/[{}]/g, '');
      variables[`var${varNum}`] = '';
    });
    setTemplateVariables(variables);
  };

  // Send template message
  const sendTemplateMessage = async () => {
    if (!selectedTemplate || !selectedConversation) return;

    // Validate all variables are filled
    const missingVars = Object.entries(templateVariables).filter(([key, value]) => !value.trim());
    if (missingVars.length > 0) {
      toast.error('Please fill in all template variables');
      return;
    }

    setSendingTemplate(true);

    try {
      const templateData = {
        templateName: selectedTemplate.name,
        language: selectedTemplate.language || 'en',
        variables: Object.values(templateVariables),
        to: selectedConversation.whatsappPhone || selectedConversation.phone,
      };

      const response = await api.post('/whatsapp/send-template', templateData);

      toast.success('Template message sent successfully');

      // Close template selector
      setShowTemplateSelector(false);
      setSelectedTemplate(null);
      setTemplateVariables({});

      // Refresh messages
      await loadMessages(selectedConversation.id, true, selectedConversation.isSupport);
    } catch (error) {
      console.error('Failed to send template message:', error);
      toast.error('Failed to send template message: ' + (error.response?.data?.error || error.message));
    } finally {
      setSendingTemplate(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);

    if (files.length === 0) return;

    // Check file sizes (limit to 5MB to be safe with base64 overhead)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    const oversizedFiles = files.filter(f => f.size > MAX_FILE_SIZE);

    if (oversizedFiles.length > 0) {
      toast.error(`File too large! Maximum size is 5MB. File "${oversizedFiles[0].name}" is ${(oversizedFiles[0].size / 1024 / 1024).toFixed(2)}MB`);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Check number of files
    if (files.length > 5) {
      toast.error('Maximum 5 files can be uploaded at once');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setSelectedFiles(files);
    // Auto-send files when selected
    sendFilesMessage(files);
  };

  // Send files as message
  const sendFilesMessage = async (files) => {
    if (!selectedConversation || uploadingFiles) return;

    setUploadingFiles(true);

    try {
      console.log('Uploading files to server:', files.length);

      // Step 1: Upload files to server first
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('files', file);
      });

      const uploadResponse = await api.uploadFiles('/upload/message-attachments', formData);
      console.log('Files uploaded successfully:', uploadResponse);

      if (!uploadResponse.attachments || uploadResponse.attachments.length === 0) {
        throw new Error('No attachments returned from server');
      }

      const filesData = uploadResponse.attachments;

      // Step 2: Create message text
      const fileNames = filesData.map(f => f.name).join(', ');
      const messageText = files.length === 1
        ? `📎 ${fileNames}`
        : `📎 ${files.length} files`;

      // Step 3: Send message with file URLs
      console.log('Sending message with file URLs:', filesData);

      // Clean up attachments to match Message model schema exactly
      const cleanAttachments = filesData.map(file => ({
        name: file.name,
        type: file.type,
        size: file.size,
        url: file.url
        // Remove any extra properties like 'filename' that might cause validation issues
      }));

      const messageData = {
        content: messageText,
        type: 'media',
        attachments: cleanAttachments
      };

      console.log('Message payload:', {
        content: messageData.content,
        type: messageData.type,
        attachmentsCount: messageData.attachments.length,
        firstAttachment: messageData.attachments[0]
      });

      let response;
      try {
        if (selectedConversation.isSupport) {
          console.log('Sending to support endpoint');
          response = await api.post('/messages/support', {
            ...messageData,
            conversationId: selectedConversation.id
          });
        } else {
          console.log('Sending to regular message endpoint');
          response = await api.messages.sendMessage(selectedConversation.id, messageData);
        }

        console.log('Message sent successfully:', response);

        // Step 4: Add message to UI
        const fileMessage = {
          id: response.message?._id || response.data?._id || response._id || `msg_${Date.now()}`,
          senderId: user?.id,
          senderName: user?.firstName ? `${user.firstName} ${user.lastName}` : 'You',
          message: messageText,
          timestamp: new Date(),
          isOwn: true,
          status: 'sent',
          attachments: cleanAttachments
        };

        setMessages(prev => [...prev, fileMessage]);
        toast.success(`${files.length} file(s) sent successfully`);

      } catch (apiError) {
        console.error('API error sending message:', apiError);
        console.error('Error details:', apiError.message);
        console.error('Error response:', apiError.response?.data);
        toast.error('Failed to send message: ' + (apiError.response?.data?.message || apiError.message));
      }
      setSelectedFiles([]);

      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      scrollToBottom();

    } catch (error) {
      console.error('Failed to upload or send files:', error);
      toast.error('Failed to send files: ' + (error.message || 'Unknown error'));

      // Clear the file input on error too
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setUploadingFiles(false);
    }
  };

  // Close emoji picker and template selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
      if (templateSelectorRef.current && !templateSelectorRef.current.contains(event.target)) {
        setShowTemplateSelector(false);
      }
    };

    if (showEmojiPicker || showTemplateSelector) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker, showTemplateSelector]);

  // Load templates when WhatsApp conversation is selected
  useEffect(() => {
    if (selectedConversation?.channel === 'WHATSAPP') {
      loadTemplates();
    }
  }, [selectedConversation?.channel]);

  // Handle ending conversation (admin only)
  const handleEndConversation = async () => {
    if (!selectedConversation || user?.role !== 'admin') return;

    const confirmEnd = window.confirm(
      `Are you sure you want to end this support conversation with ${selectedConversation.name}? The customer will no longer be able to send messages.`
    );

    if (!confirmEnd) return;

    try {
      await api.post(`/messages/support/${selectedConversation.id}/end`);

      toast.success('Conversation ended successfully');

      // Reload conversations to update the list
      await loadConversations();

      // Clear selected conversation
      setSelectedConversation(null);
      setMessages([]);
    } catch (error) {
      console.error('Failed to end conversation:', error);
      toast.error('Failed to end conversation: ' + (error.response?.data?.message || error.message));
    }
  };

  // Handle completing conversation (customer only)
  const handleCompleteConversation = async () => {
    if (!selectedConversation || user?.role === 'admin') return;

    const confirmComplete = window.confirm(
      'Are you sure you want to mark this support conversation as complete? You will no longer be able to send messages.'
    );

    if (!confirmComplete) return;

    try {
      await api.post(`/messages/support/${selectedConversation.id}/complete`);

      toast.success('Conversation marked as complete');

      // Reload conversations to update the list
      await loadConversations();

      // Clear selected conversation
      setSelectedConversation(null);
      setMessages([]);
    } catch (error) {
      console.error('Failed to complete conversation:', error);
      toast.error('Failed to complete conversation: ' + (error.response?.data?.message || error.message));
    }
  };

  // Unread message functions
  const updateUnreadCount = (conversationId, count) => {
    setUnreadCounts(prev => ({
      ...prev,
      [conversationId]: count
    }));
  };

  const markConversationAsRead = (conversationId) => {
    setUnreadCounts(prev => ({
      ...prev,
      [conversationId]: 0
    }));
  };

  const getTotalUnreadCount = () => {
    return Object.values(unreadCounts).reduce((total, count) => total + count, 0);
  };

  // Scroll to bottom function - only scroll the messages container
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    loadConversations();
  }, [user]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      console.log('Loading conversations for user:', user.role); // Debug log

      if (user.role === 'admin') {
        // For admin users, load support conversations
        console.log('📞 Loading admin support conversations...');
        const response = await api.get('/support-chat/conversations/admin');
        console.log('📥 Admin conversations response:', response);
        const supportConversations = response.data?.conversations || response.conversations || [];
        console.log('📋 Found support conversations:', supportConversations.length);

        const transformedConversations = supportConversations.map(conversation => {
          // Get last message from messages array
          const lastMsg = conversation.messages?.[conversation.messages.length - 1];

          return {
            id: conversation._id,
            name: conversation.user ? `${conversation.user.firstName} ${conversation.user.lastName}` : 'Customer',
            lastMessage: lastMsg?.content || conversation.subject || 'New support request',
            timestamp: new Date(lastMsg?.createdAt || conversation.createdAt),
            avatar: null,
            isVendor: false,
            isCustomer: true,
            isSupport: true,
            status: conversation.status === 'ACTIVE' ? 'online' : 'offline',  // Changed from 'active' to 'ACTIVE'
            jobTitle: `Support: ${conversation.conversationNumber || 'New'}`,
            jobId: conversation._id,
            unreadCount: conversation.unreadCount || 0
          };
        });

        // Update unread counts
        transformedConversations.forEach(conv => {
          updateUnreadCount(conv.id, conv.unreadCount);
        });

        setConversations(transformedConversations);
        if (transformedConversations.length > 0) {
          setSelectedConversation(transformedConversations[0]);
          // Pass isSupport parameter to avoid race condition
          loadMessages(transformedConversations[0].id, false, transformedConversations[0].isSupport);
        }

        // Return transformed conversations so they can be used immediately
        return transformedConversations;
      } else {
        // Regular user conversations - load BOTH job conversations AND support conversations
        const [jobResponse, supportResponse] = await Promise.all([
          api.messages.getConversations(),
          api.get('/support-chat/conversations/my').catch(() => ({ conversations: [] }))
        ]);

        console.log('Job conversations response:', jobResponse); // Debug log
        console.log('Support conversations response:', supportResponse); // Debug log

        const realConversations = jobResponse.conversations || [];
        const supportConversations = supportResponse.data?.conversations || supportResponse.conversations || [];

        // Transform job conversations
        const transformedJobConversations = realConversations.map(conversation => {
          console.log('Processing job conversation:', conversation); // Debug log
          return {
            id: conversation._id,
            name: getConversationName(conversation),
            lastMessage: conversation.lastMessage?.content || '',
            timestamp: new Date(conversation.lastMessage?.createdAt || conversation.createdAt),
            avatar: null,
            isVendor: user.role === 'customer' && conversation.vendorId,
            isCustomer: user.role === 'vendor' && conversation.customerId,
            isSupport: false,
            status: 'offline',
            jobTitle: conversation.jobNumber ? `Job #${conversation.jobNumber}` : null,
            jobId: conversation._id,
            unreadCount: conversation.unreadCount || 0
          };
        });

        // Transform support conversations
        const transformedSupportConversations = supportConversations.map(conversation => {
          console.log('Processing support conversation:', conversation); // Debug log
          return {
            id: conversation._id,
            name: 'Support Team',
            lastMessage: conversation.lastMessage?.content || 'New support request',
            timestamp: new Date(conversation.lastMessage?.createdAt || conversation.createdAt),
            avatar: null,
            isVendor: false,
            isCustomer: false,
            isSupport: true,
            status: conversation.status === 'ACTIVE' ? 'online' : 'offline',
            jobTitle: 'Support Request',
            jobId: conversation._id,
            unreadCount: conversation.unreadCount || 0
          };
        });

        // Combine and sort by timestamp
        const allConversations = [...transformedJobConversations, ...transformedSupportConversations]
          .sort((a, b) => b.timestamp - a.timestamp);

        // Update unread counts
        allConversations.forEach(conv => {
          updateUnreadCount(conv.id, conv.unreadCount);
        });

        setConversations(allConversations);
        if (allConversations.length > 0) {
          setSelectedConversation(allConversations[0]);
          // Pass isSupport parameter to avoid race condition
          loadMessages(allConversations[0].id, false, allConversations[0].isSupport);
        } else {
          console.log('No conversations found for user'); // Debug log
        }

        // Return transformed conversations so they can be used immediately
        return allConversations;
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
      toast.error('Failed to load conversations: ' + error.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get conversation name based on user role
  const getConversationName = (conversation) => {
    console.log('getConversationName - user role:', user.role, 'conversation:', conversation);

    if (user.role === 'customer') {
      // Customer sees vendor name
      const vendorName = conversation.vendor
        ? `${conversation.vendor.firstName || ''} ${conversation.vendor.lastName || ''}`.trim()
        : 'Service Provider';
      console.log('Vendor name:', vendorName);
      return vendorName;
    } else if (user.role === 'vendor') {
      // Vendor sees customer name
      const customerName = conversation.customer
        ? `${conversation.customer.firstName || ''} ${conversation.customer.lastName || ''}`.trim()
        : 'Customer';
      console.log('Customer name:', customerName);
      return customerName;
    } else {
      // Admin sees both
      const customerName = conversation.customer
        ? `${conversation.customer.firstName || ''} ${conversation.customer.lastName || ''}`.trim()
        : 'Customer';
      const vendorName = conversation.vendor
        ? `${conversation.vendor.firstName || ''} ${conversation.vendor.lastName || ''}`.trim()
        : 'Vendor';
      return `${customerName} ↔ ${vendorName}`;
    }
  };

  const loadMessages = async (conversationId, forceRefresh = false, isSupport = null) => {
    // Debounce multiple rapid calls to the same conversation (unless forced)
    if (!forceRefresh && selectedConversation?.id === conversationId && messages.length > 0) {
      return; // Already loaded this conversation
    }

    try {
      // If isSupport is not explicitly provided, use selectedConversation (fallback for compatibility)
      const isSupportConv = isSupport !== null ? isSupport : selectedConversation?.isSupport;

      console.log('=== LOADING MESSAGES ===');
      console.log('Conversation ID:', conversationId);
      console.log('Current user:', user);
      console.log('Is support conversation:', isSupportConv);

      // Get real messages from API - use different endpoint for support conversations
      let response;
      let realMessages = [];

      if (isSupportConv) {
        // Support conversation endpoint - returns full conversation with embedded messages
        console.log('🔍 Fetching support conversation with ID:', conversationId);
        try {
          response = await api.get(`/support-chat/conversations/${conversationId}`);
          console.log('✅ Support conversation API response:', response);
          realMessages = response.conversation?.messages || response.data?.conversation?.messages || [];
          console.log('📨 Extracted messages:', realMessages);
        } catch (error) {
          console.error('❌ Error fetching support conversation:', error);
          console.error('Error details:', error.response?.data);
          throw error;
        }
      } else {
        // Regular job messages
        response = await api.messages.getMessages(conversationId);
        console.log('Messages API response:', response);
        realMessages = response.messages || [];
      }

      console.log('Raw messages from API:', realMessages);

      // Transform the backend data to match the UI format
      const transformedMessages = realMessages.map((message, index) => {
        console.log(`\n--- Processing Message ${index + 1} ---`);
        console.log('Raw message:', message);

        const senderId = message.senderId?._id || message.senderId;
        const userId = user?.id; // Fix: use user.id instead of user._id

        console.log('Extracted senderId:', senderId);
        console.log('Current userId:', userId);
        console.log('senderId type:', typeof senderId);
        console.log('userId type:', typeof userId);

        const isOwn = senderId && userId && senderId.toString() === userId.toString();
        console.log('Comparison result - isOwn:', isOwn);
        console.log('senderId.toString():', senderId?.toString());
        console.log('userId.toString():', userId?.toString());

        // Process attachments to ensure URLs are properly set
        let attachments = [];
        if (message.attachments && Array.isArray(message.attachments)) {
          console.log('Processing attachments for message:', message._id, message.attachments);
          attachments = message.attachments.map(att => {
            let url = att.url || att.data || '';
            
            // Convert relative URLs to absolute URLs
            if (url && url.startsWith('/uploads/')) {
              url = `http://localhost:5000${url}`;
            }
            
            const processedAtt = {
              name: att.name,
              type: att.type,
              size: att.size,
              url: url
            };
            console.log('Processed attachment:', processedAtt.name, 'Final URL:', processedAtt.url);
            return processedAtt;
          });
        }

        const transformed = {
          id: message._id,
          senderId: senderId,
          senderName: getSenderName(message.senderId),
          message: message.content,
          timestamp: new Date(message.createdAt),
          isOwn: isOwn,
          status: message.status?.toLowerCase() || 'sent',
          attachments: attachments, // Processed attachments with proper URLs
          isEdited: message.isEdited || false,
          editedAt: message.editedAt ? new Date(message.editedAt) : null
        };

        console.log('Transformed message:', transformed);
        return transformed;
      });

      console.log('All transformed messages:', transformedMessages);
      setMessages(transformedMessages);
    } catch (error) {
      console.error('Failed to load messages:', error);
      toast.error('Failed to load messages: ' + error.message);
    }
  };

  // Helper function to get sender name
  const getSenderName = (sender) => {
    if (typeof sender === 'object' && sender !== null) {
      return `${sender.firstName || ''} ${sender.lastName || ''}`.trim() || sender.email || 'Unknown';
    }
    return 'Unknown';
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || sendingMessage) return;

    const messageText = newMessage.trim();
    setSendingMessage(true);
    setNewMessage(''); // Clear input immediately

    try {
      console.log('Sending message to conversation:', selectedConversation.id); // Debug log

      // Handle support conversations differently
      if (selectedConversation.isSupport) {
        // For support conversations, send to support-chat endpoint
        try {
          const response = await api.post(`/support-chat/conversations/${selectedConversation.id}/messages`, {
            content: messageText,
            attachments: []
          });

          console.log('Support message response:', response); // Debug log

          // Add the sent message immediately to the UI
          const newMessageObj = {
            id: `msg_${Date.now()}_user`,
            senderId: user?.id || user?._id,
            senderName: user?.firstName ? `${user.firstName} ${user.lastName}` : 'You',
            message: messageText,
            timestamp: new Date(),
            isOwn: true,
            status: 'sent'
          };

          setMessages(prev => [...prev, newMessageObj]);

          // Scroll to bottom immediately
          setTimeout(scrollToBottom, 100);

        } catch (supportError) {
          console.log('Support API not available, using fallback');

          // Fallback: Add message directly to UI for support conversations
          const newMessageObj = {
            id: `msg_${Date.now()}_user`,
            senderId: user?.id || user?._id,
            senderName: user?.firstName ? `${user.firstName} ${user.lastName}` : 'You',
            message: messageText,
            timestamp: new Date(),
            isOwn: true,
            status: 'sent'
          };

          setMessages(prev => [...prev, newMessageObj]);
          setTimeout(scrollToBottom, 100);
        }
      } else {
        // Add the sent message immediately to the UI for better UX
        const tempMessage = {
          id: `temp_${Date.now()}`,
          senderId: user?.id,
          senderName: user?.firstName ? `${user.firstName} ${user.lastName}` : 'You',
          message: messageText,
          timestamp: new Date(),
          isOwn: true,
          status: 'sending'
        };

        setMessages(prev => [...prev, tempMessage]);

        // Scroll to bottom immediately when adding the temp message
        setTimeout(scrollToBottom, 100);

        // Regular conversation - send via existing API
        const response = await api.messages.sendMessage(selectedConversation.id, {
          content: messageText,
          type: 'text'
        });

        console.log('Send message response:', response); // Debug log

        // Update the temporary message with real data
        const realMessage = {
          id: response.message?.id || response.id || tempMessage.id,
          senderId: user?.id,
          senderName: user?.firstName ? `${user.firstName} ${user.lastName}` : 'You',
          message: messageText,
          timestamp: new Date(response.message?.createdAt || tempMessage.timestamp),
          isOwn: true,
          status: 'sent'
        };

        setMessages(prev => prev.map(msg =>
          msg.id === tempMessage.id ? realMessage : msg
        ));

      }
    } catch (error) {
      console.error('Failed to send message:', error);
      console.error('Error details:', error.response?.data || error.message);
      console.error('Error status:', error.response?.status);
      console.error('Error stack:', error.stack);

      // Remove any temporary messages that failed
      setMessages(prev => prev.filter(msg => !msg.id.toString().startsWith('temp_')));

      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      toast.error('Failed to send message: ' + errorMessage);

      // Restore the message text if sending failed
      setNewMessage(messageText);
    } finally {
      setSendingMessage(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return timestamp.toLocaleDateString();
  };

  const handleTabSelect = (tabName) => {
    setSelectedTab(tabName);
    localStorage.setItem('unified-messages-selected-tab', tabName);
  };

  const connectToSupport = async () => {
    if (connectingToSupport) return;

    setConnectingToSupport(true);

    try {
      // Create a proper SupportConversation (not a Job)
      const response = await api.post('/support-chat/conversations', {
        subject: 'General Support Request',
        category: 'GENERAL_INQUIRY',
        priority: 'NORMAL',
        message: 'I need assistance with my account/service. Please help.'
      });

      const newConversation = response.conversation || response.data?.conversation || response;
      const newConversationId = newConversation._id || newConversation.id;
      console.log('✅ Created support conversation:', newConversation);
      console.log('📝 Conversation ID:', newConversationId);
      console.log('📝 Full response:', response);

      // Reload conversations to show the new support conversation
      const loadedConversations = await loadConversations();

      // Find and select the newly created support conversation
      if (loadedConversations && Array.isArray(loadedConversations)) {
        // Find the support conversation that matches the new ID
        const newSupportConv = loadedConversations.find(conv =>
          conv.id === newConversationId && conv.isSupport
        );

        if (newSupportConv) {
          console.log('✅ Found and selected new support conversation:', newSupportConv);
          setSelectedConversation(newSupportConv);
          // Pass isSupport=true to avoid race condition
          loadMessages(newSupportConv.id, false, true);
        } else {
          console.log('⚠️ Could not find new support conversation, ID:', newConversationId);
          console.log('Available conversations:', loadedConversations);

          // Fallback: select first support conversation
          const anySupportConv = loadedConversations.find(conv => conv.isSupport);
          if (anySupportConv) {
            setSelectedConversation(anySupportConv);
            loadMessages(anySupportConv.id, false, true);
          }
        }
      }

      toast.success('Connected to support team!');

    } catch (error) {
      console.error('❌ Failed to connect to support:', error);
      toast.error('Failed to create support request. Please try again.');
    } finally {
      setConnectingToSupport(false);
    }
  };

  // Handle delete message
  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      await api.messages.deleteMessage(messageId);

      // Remove message from UI
      setMessages(prev => prev.filter(msg => msg.id !== messageId));

      toast.success('Message deleted successfully');
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error('Failed to delete message: ' + (error.response?.data?.message || error.message));
    }
  };

  // Handle start editing message
  const handleStartEdit = (message) => {
    setEditingMessageId(message.id);
    setEditingMessageContent(message.message);
  };

  // Handle cancel editing
  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditingMessageContent('');
  };

  // Handle save edited message
  const handleSaveEdit = async (messageId) => {
    if (!editingMessageContent.trim()) {
      toast.error('Message cannot be empty');
      return;
    }

    try {
      const response = await api.messages.editMessage(messageId, editingMessageContent.trim());

      // Update message in UI
      setMessages(prev => prev.map(msg =>
        msg.id === messageId
          ? {
              ...msg,
              message: editingMessageContent.trim(),
              isEdited: true,
              editedAt: new Date()
            }
          : msg
      ));

      setEditingMessageId(null);
      setEditingMessageContent('');

      toast.success('Message updated successfully');
    } catch (error) {
      console.error('Failed to edit message:', error);
      toast.error('Failed to edit message: ' + (error.response?.data?.message || error.message));
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-4 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600 font-medium">Loading your messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Modern Hero Header - Matching About Page Style */}
      <section className="relative overflow-hidden bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700">
        {/* Geometric patterns */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500 rounded-full opacity-20 transform translate-x-32 -translate-y-32"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-orange-700 rounded-full opacity-30 transform -translate-x-24 translate-y-24"></div>

        <div className="relative container mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center justify-between text-white"
          >
            {/* Left Section */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2.5 hover:bg-white/10 rounded-xl transition-colors duration-200 backdrop-blur-sm"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold flex items-center">
                  <MessageSquare className="w-8 h-8 mr-3" />
                  {user?.role === 'admin' ? 'Support Center' : 'Messages'}
                </h1>
                <p className="text-sm text-orange-100 mt-1">
                  {user?.role === 'admin'
                    ? 'Manage customer support conversations'
                    : 'Stay connected with your service providers'
                  }
                </p>
              </div>
            </div>

            {/* Right Section - Stats */}
            <div className="flex gap-4">
              <div className="bg-white/20 backdrop-blur-sm px-5 py-3 rounded-xl border border-white/30">
                <div className="text-2xl font-bold text-white">{conversations.length}</div>
                <div className="text-xs text-orange-100 font-medium">Conversations</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-5 py-3 rounded-xl border border-white/30">
                <div className="text-2xl font-bold text-white">{getTotalUnreadCount()}</div>
                <div className="text-xs text-orange-100 font-medium">Unread</div>
              </div>
            </div>
          </motion.div>

          {/* Quick Actions - Only show for non-admin users */}
          {user?.role !== 'admin' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex gap-3 mt-6"
            >
              <button
                onClick={() => {
                  setChannelFilter('SUPPORT');
                  connectToSupport();
                }}
                disabled={connectingToSupport}
                className="px-5 py-2.5 bg-white text-orange-600 rounded-xl text-sm font-semibold hover:bg-orange-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-xl transform hover:-translate-y-0.5 duration-200"
              >
                {connectingToSupport ? '🔄 Connecting...' : '✉️ Contact Support'}
              </button>
              <button
                onClick={() => handleTabSelect('emergency-contact')}
                className="px-5 py-2.5 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 transition-all shadow-md hover:shadow-xl transform hover:-translate-y-0.5 duration-200"
              >
                📞 Emergency
              </button>
            </motion.div>
          )}
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden" style={{ height: 'calc(100vh - 240px)' }}>
          <div className="flex h-full">
            {/* Conversations List */}
            <div className="w-2/5 border-r border-gray-200 flex flex-col bg-gray-50">
              {/* Search Header */}
              <div className="p-5 border-b border-gray-200 bg-white">
                <div className="relative mb-4">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    className="w-full pl-12 pr-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white transition-all duration-200"
                  />
                </div>

                {/* Channel Filter Tabs */}
                <div className="flex gap-2 overflow-x-auto">
                  <button
                    onClick={() => setChannelFilter('ALL')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                      channelFilter === 'ALL'
                        ? 'bg-orange-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setChannelFilter('WEB')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center gap-1 ${
                      channelFilter === 'WEB'
                        ? 'bg-orange-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <MessageSquare className="w-4 h-4" />
                    Web
                  </button>
                  <button
                    onClick={() => setChannelFilter('WHATSAPP')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center gap-1 ${
                      channelFilter === 'WHATSAPP'
                        ? 'bg-green-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp
                  </button>
                  <button
                    onClick={() => setChannelFilter('SUPPORT')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center gap-1 ${
                      channelFilter === 'SUPPORT'
                        ? 'bg-orange-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Star className="w-4 h-4" />
                    Support
                  </button>
                </div>
              </div>

              {/* Conversations */}
              <div className="flex-1 overflow-y-auto">
                {conversations
                  .filter(conv => {
                    if (channelFilter === 'ALL') return true;
                    if (channelFilter === 'SUPPORT') return conv.isSupport;
                    if (channelFilter === 'WHATSAPP') return conv.channel === 'WHATSAPP';
                    if (channelFilter === 'WEB') return !conv.isSupport && conv.channel !== 'WHATSAPP';
                    return true;
                  })
                  .map((conversation) => (
                  <div
                    key={conversation.id}
                    onClick={() => {
                      setSelectedConversation(conversation);
                      // Pass isSupport parameter to avoid race condition with state update
                      loadMessages(conversation.id, false, conversation.isSupport);

                      // Mark conversation as read via API (support conversations are auto-marked when fetched)
                      if (!conversation.isSupport) {
                        api.messages.markAsRead(conversation.id)
                          .then(() => {
                            markConversationAsRead(conversation.id);
                          })
                          .catch((error) => {
                            console.error('Failed to mark as read on backend:', error);
                            // Still mark as read locally for better UX
                            markConversationAsRead(conversation.id);
                          });
                      } else {
                        // Support conversations are marked as read when fetched, just update UI
                        markConversationAsRead(conversation.id);
                      }
                    }}
                    className={`p-5 border-b border-gray-100 cursor-pointer hover:bg-white transition-all duration-200 ${
                      selectedConversation?.id === conversation.id ? 'bg-orange-50 border-r-4 border-orange-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center w-full">
                        <div className="relative">
                          <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-md ${
                            conversation.channel === 'WHATSAPP'
                              ? 'bg-gradient-to-br from-green-400 to-green-600'
                              : 'bg-gradient-to-br from-orange-400 to-orange-600'
                          }`}>
                            {conversation.channel === 'WHATSAPP' ? (
                              <MessageCircle className="w-7 h-7 text-white" />
                            ) : conversation.isSupport ? (
                              <Star className="w-7 h-7 text-white" />
                            ) : (
                              <User className="w-7 h-7 text-white" />
                            )}
                          </div>
                          {conversation.status === 'online' && (
                            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white shadow-sm"></div>
                          )}
                          {unreadCounts[conversation.id] > 0 && (
                            <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold border-2 border-white shadow-sm">
                              {unreadCounts[conversation.id] > 9 ? '9+' : unreadCounts[conversation.id]}
                            </div>
                          )}
                        </div>
                        <div className="ml-4 flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <h4 className={`font-medium text-base ${unreadCounts[conversation.id] > 0 ? 'text-gray-900 font-bold' : 'text-gray-900'}`}>
                                {conversation.name}
                              </h4>
                              {conversation.channel === 'WHATSAPP' && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full flex items-center gap-1">
                                  <MessageCircle className="w-3 h-3" />
                                  WhatsApp
                                </span>
                              )}
                              {unreadCounts[conversation.id] > 0 && (
                                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                              )}
                            </div>
                            <span className="text-sm text-gray-500">{formatTimestamp(conversation.timestamp)}</span>
                          </div>
                          {conversation.jobTitle && (
                            <p className="text-sm text-orange-600 font-medium mb-1">{conversation.jobTitle}</p>
                          )}
                          {conversation.whatsappPhone && (
                            <p className="text-xs text-gray-500 mb-1">
                              <Phone className="w-3 h-3 inline mr-1" />
                              {conversation.whatsappPhone}
                            </p>
                          )}
                          <p className="text-base text-gray-600 truncate">{conversation.lastMessage}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 flex flex-col">
              {selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center mr-4 shadow-lg ${
                          selectedConversation.channel === 'WHATSAPP'
                            ? 'bg-gradient-to-br from-green-500 to-green-600'
                            : 'bg-gradient-to-br from-orange-500 to-orange-600'
                        }`}>
                          {selectedConversation.channel === 'WHATSAPP' ? (
                            <MessageCircle className="w-8 h-8 text-white" />
                          ) : selectedConversation.isSupport ? (
                            <Star className="w-8 h-8 text-white" />
                          ) : (
                            <User className="w-8 h-8 text-white" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-gray-900 text-xl">{selectedConversation.name}</h3>
                            {selectedConversation.channel === 'WHATSAPP' && (
                              <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full flex items-center gap-1">
                                <MessageCircle className="w-3 h-3" />
                                WhatsApp
                              </span>
                            )}
                          </div>
                          {selectedConversation.whatsappPhone && (
                            <p className="text-sm text-gray-600 mt-0.5">
                              <Phone className="w-3 h-3 inline mr-1" />
                              {selectedConversation.whatsappPhone}
                            </p>
                          )}
                          <p className="text-base font-medium mt-1">
                            {selectedConversation.status === 'online' ? (
                              <span className="text-green-600 flex items-center">
                                <span className="w-2.5 h-2.5 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                                Online
                              </span>
                            ) : (
                              <span className="text-gray-500 flex items-center">
                                <span className="w-2.5 h-2.5 bg-gray-400 rounded-full mr-2"></span>
                                Offline
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {user?.role === 'admin' && selectedConversation?.isSupport && (
                          <button
                            onClick={handleEndConversation}
                            className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all duration-200 flex items-center space-x-2"
                          >
                            <X className="w-5 h-5" />
                            <span className="text-sm font-medium">End Conversation</span>
                          </button>
                        )}
                        {user?.role !== 'admin' && selectedConversation?.isSupport && (
                          <button
                            onClick={handleCompleteConversation}
                            className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all duration-200 flex items-center space-x-2"
                          >
                            <CheckCheck className="w-5 h-5" />
                            <span className="text-sm font-medium">Mark as Complete</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div
                    ref={messagesContainerRef}
                    className="flex-1 overflow-y-auto p-6 bg-gradient-to-b from-gray-50 to-white"
                  >
                    {messages.map((message, index) => (
                      <div
                        key={message.id}
                        className={`flex mb-6 ${message.isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-2xl ${message.isOwn ? 'order-2' : 'order-1'}`}>
                          {!message.isOwn && (
                            <div className="flex items-center mb-2 ml-1">
                              <div className="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-white text-sm font-semibold mr-2 shadow-sm">
                                {message.senderName?.[0] || 'U'}
                              </div>
                              <span className="text-sm font-semibold text-gray-700">
                                {message.senderName}
                              </span>
                              <span className="text-xs text-gray-500 ml-2">
                                {formatTimestamp(message.timestamp)}
                              </span>
                              {message.isEdited && (
                                <span
                                  className="text-xs text-gray-500 ml-2 italic"
                                  title={message.editedAt ? `Edited ${formatTimestamp(message.editedAt)}` : 'Edited'}
                                >
                                  (edited)
                                </span>
                              )}
                            </div>
                          )}
                          <div
                            className={`px-6 py-4 ${
                              message.isOwn
                                ? 'bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-3xl rounded-tr-md shadow-lg'
                                : 'bg-white text-gray-800 rounded-3xl rounded-tl-md shadow-md border border-gray-200'
                            }`}
                          >
                            {message.isOwn && (
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-orange-100">
                                    {formatTimestamp(message.timestamp)}
                                  </span>
                                  <span className="text-sm font-semibold text-orange-100">You</span>
                                  {message.isEdited && (
                                    <span
                                      className="text-xs text-orange-200 italic"
                                      title={message.editedAt ? `Edited ${formatTimestamp(message.editedAt)}` : 'Edited'}
                                    >
                                      (edited)
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => handleStartEdit(message)}
                                    className="p-1 hover:bg-orange-400/30 rounded transition-colors"
                                    title="Edit message"
                                  >
                                    <Edit2 className="w-4 h-4 text-orange-100" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteMessage(message.id)}
                                    className="p-1 hover:bg-orange-400/30 rounded transition-colors"
                                    title="Delete message"
                                  >
                                    <Trash2 className="w-4 h-4 text-orange-100" />
                                  </button>
                                </div>
                              </div>
                            )}
                            {editingMessageId === message.id ? (
                              <div className="space-y-2">
                                <textarea
                                  value={editingMessageContent}
                                  onChange={(e) => setEditingMessageContent(e.target.value)}
                                  className="w-full px-4 py-2 border-2 border-orange-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-800 resize-none"
                                  rows={3}
                                  autoFocus
                                />
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleSaveEdit(message.id)}
                                    className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
                                  >
                                    <Save className="w-4 h-4" />
                                    Save
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-lg leading-relaxed break-words">{message.message}</p>
                            )}

                            {/* Show attachments if available */}
                            {message.attachments && message.attachments.length > 0 && (
                              <div className="mt-3 space-y-2">
                                {message.attachments.map((file, idx) => (
                                  <div key={idx}>
                                    {/* Show image preview for images */}
                                    {file.type?.startsWith('image/') && file.url ? (
                                      <div className="rounded-lg overflow-hidden max-w-xs">
                                        <img
                                          src={file.url}
                                          alt={file.name}
                                          className="w-full h-auto object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                          onClick={() => window.open(file.url, '_blank')}
                                        />
                                        <div className={`flex items-center gap-2 p-2 text-xs ${message.isOwn ? 'bg-orange-400/30 text-orange-100' : 'bg-gray-100 text-gray-700'}`}>
                                          <ImageIcon className="w-4 h-4" />
                                          <span className="truncate flex-1">{file.name}</span>
                                          <span className="opacity-75">
                                            {(file.size / 1024).toFixed(1)} KB
                                          </span>
                                        </div>
                                      </div>
                                    ) : file.type?.startsWith('video/') && file.url ? (
                                      /* Show video preview for videos */
                                      <div className="rounded-lg overflow-hidden max-w-md">
                                        <video
                                          controls
                                          className="w-full h-auto"
                                          preload="metadata"
                                        >
                                          <source src={file.url} type={file.type} />
                                          Your browser does not support the video tag.
                                        </video>
                                        <div className={`flex items-center gap-2 p-2 text-xs ${message.isOwn ? 'bg-orange-400/30 text-orange-100' : 'bg-gray-100 text-gray-700'}`}>
                                          <VideoIcon className="w-4 h-4" />
                                          <span className="truncate flex-1">{file.name}</span>
                                          <span className="opacity-75">
                                            {(file.size / 1024 / 1024).toFixed(2)} MB
                                          </span>
                                        </div>
                                      </div>
                                    ) : (
                                      /* Show file info for other files */
                                      <div className={`flex items-center gap-2 p-3 rounded-lg ${message.isOwn ? 'bg-orange-400/30' : 'bg-gray-100'}`}>
                                        <File className="w-5 h-5" />
                                        <div className="flex-1 min-w-0">
                                          <div className="text-sm font-medium truncate">{file.name}</div>
                                          <div className="text-xs opacity-75">
                                            {file.size > 1024 * 1024
                                              ? `${(file.size / 1024 / 1024).toFixed(2)} MB`
                                              : `${(file.size / 1024).toFixed(1)} KB`
                                            }
                                          </div>
                                        </div>
                                        {file.url && (
                                          <a
                                            href={file.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`text-xs font-medium hover:underline ${message.isOwn ? 'text-orange-100' : 'text-orange-600'}`}
                                          >
                                            Download
                                          </a>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {message.isOwn && (
                              <div className="flex items-center justify-end mt-2 text-xs text-orange-100">
                                {message.status === 'sending' || message.status === 'pending' ? (
                                  <>
                                    <Clock className="w-4 h-4 mr-1" />
                                    <span>Sending</span>
                                  </>
                                ) : message.status === 'sent' ? (
                                  <>
                                    <Check className="w-4 h-4 mr-1" />
                                    <span>Sent</span>
                                  </>
                                ) : message.status === 'delivered' ? (
                                  <>
                                    <CheckCheck className="w-4 h-4 mr-1" />
                                    <span>Delivered</span>
                                  </>
                                ) : message.status === 'read' ? (
                                  <>
                                    <CheckCheck className="w-4 h-4 mr-1 text-blue-300" />
                                    <span>Read</span>
                                  </>
                                ) : message.status === 'failed' ? (
                                  <>
                                    <X className="w-4 h-4 mr-1 text-red-300" />
                                    <span>Failed</span>
                                  </>
                                ) : (
                                  <>
                                    <CheckCheck className="w-4 h-4 mr-1" />
                                    <span>Delivered</span>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Message Input */}
                  <div className="p-6 border-t border-gray-200 bg-white">
                    {selectedConversation.isEnded ? (
                      <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 text-center">
                        <p className="text-red-800 font-semibold text-lg">
                          🔒 This conversation has been ended by an admin
                        </p>
                        <p className="text-red-600 text-sm mt-1">
                          You can no longer send messages in this conversation
                        </p>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-3 relative">
                        {/* Hidden file input */}
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept="image/*,video/*,.pdf,.doc,.docx"
                          onChange={handleFileSelect}
                          className="hidden"
                        />

                        {/* File upload button */}
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingFiles}
                          className="p-3 text-gray-400 hover:text-orange-600 rounded-xl hover:bg-orange-50 transition-all duration-200 disabled:opacity-50"
                          title="Attach files"
                        >
                          <Paperclip className="w-6 h-6" />
                        </button>

                        {/* Template selector button (WhatsApp only) */}
                        {selectedConversation?.channel === 'WHATSAPP' && (
                          <div className="relative">
                            <button
                              onClick={() => setShowTemplateSelector(!showTemplateSelector)}
                              className="p-3 text-green-500 hover:text-green-600 rounded-xl hover:bg-green-50 transition-all duration-200"
                              title="Send WhatsApp Template"
                            >
                              <FileText className="w-6 h-6" />
                            </button>

                            {/* Template selector modal */}
                            {showTemplateSelector && (
                              <div
                                ref={templateSelectorRef}
                                className="absolute bottom-full left-0 mb-2 bg-white border-2 border-gray-200 rounded-2xl shadow-2xl p-4 w-96 max-h-[500px] overflow-y-auto z-50"
                              >
                                <div className="mb-3">
                                  <h4 className="text-lg font-bold text-gray-800 mb-1 flex items-center">
                                    <MessageCircle className="w-5 h-5 text-green-500 mr-2" />
                                    WhatsApp Templates
                                  </h4>
                                  <p className="text-xs text-gray-500">
                                    Select a pre-approved template to send
                                  </p>
                                </div>

                                {!selectedTemplate ? (
                                  /* Template list */
                                  <div className="space-y-2">
                                    {templates.length === 0 ? (
                                      <p className="text-sm text-gray-500 text-center py-4">
                                        No templates available
                                      </p>
                                    ) : (
                                      templates.map((template) => (
                                        <button
                                          key={template._id}
                                          onClick={() => handleTemplateSelect(template)}
                                          className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all"
                                        >
                                          <div className="font-semibold text-gray-800 text-sm">
                                            {template.name}
                                          </div>
                                          <div className="text-xs text-gray-500 mt-1">
                                            {template.category}
                                          </div>
                                          {template.components?.[0]?.text && (
                                            <div className="text-xs text-gray-600 mt-2 line-clamp-2">
                                              {template.components[0].text}
                                            </div>
                                          )}
                                        </button>
                                      ))
                                    )}
                                  </div>
                                ) : (
                                  /* Template variable form */
                                  <div>
                                    <button
                                      onClick={() => {
                                        setSelectedTemplate(null);
                                        setTemplateVariables({});
                                      }}
                                      className="text-sm text-gray-500 hover:text-gray-700 mb-3 flex items-center"
                                    >
                                      <ArrowLeft className="w-4 h-4 mr-1" />
                                      Back to templates
                                    </button>

                                    <div className="mb-3">
                                      <div className="font-semibold text-gray-800 mb-2">
                                        {selectedTemplate.name}
                                      </div>
                                      <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg mb-3">
                                        {selectedTemplate.components?.[0]?.text}
                                      </div>
                                    </div>

                                    {Object.keys(templateVariables).length > 0 && (
                                      <div className="space-y-2 mb-3">
                                        <p className="text-sm font-semibold text-gray-700">
                                          Fill in variables:
                                        </p>
                                        {Object.keys(templateVariables).map((key, index) => (
                                          <input
                                            key={key}
                                            type="text"
                                            placeholder={`Variable ${index + 1}`}
                                            value={templateVariables[key]}
                                            onChange={(e) =>
                                              setTemplateVariables((prev) => ({
                                                ...prev,
                                                [key]: e.target.value,
                                              }))
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                                          />
                                        ))}
                                      </div>
                                    )}

                                    <button
                                      onClick={sendTemplateMessage}
                                      disabled={sendingTemplate}
                                      className="w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-semibold"
                                    >
                                      {sendingTemplate ? 'Sending...' : 'Send Template'}
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Emoji picker button */}
                        <div className="relative">
                          <button
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            className="p-3 text-gray-400 hover:text-orange-600 rounded-xl hover:bg-orange-50 transition-all duration-200"
                            title="Add emoji"
                          >
                            <Smile className="w-6 h-6" />
                          </button>

                          {/* Emoji picker dropdown */}
                          {showEmojiPicker && (
                            <div
                              ref={emojiPickerRef}
                              className="absolute bottom-full left-0 mb-2 bg-white border-2 border-gray-200 rounded-2xl shadow-2xl p-4 w-80 max-h-96 overflow-y-auto z-50"
                            >
                              <div className="mb-3">
                                <h4 className="text-sm font-semibold text-gray-700 mb-2">Frequently Used</h4>
                                <div className="grid grid-cols-10 gap-1">
                                  {commonEmojis.map((emoji, index) => (
                                    <button
                                      key={index}
                                      onClick={() => handleEmojiClick(emoji)}
                                      className="text-2xl hover:bg-orange-50 rounded-lg p-1 transition-colors"
                                      title={emoji}
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex-1 flex flex-col gap-2">
                          <textarea
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())}
                            placeholder="Type your message..."
                            maxLength={selectedConversation?.channel === 'WHATSAPP' ? 4096 : 2000}
                            rows={2}
                            className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all text-lg placeholder-gray-400 resize-none"
                            disabled={sendingMessage || uploadingFiles}
                          />
                          {newMessage.length > 0 && (
                            <div className="flex items-center justify-between px-2">
                              <span className={`text-sm ${
                                newMessage.length >= (selectedConversation?.channel === 'WHATSAPP' ? 4096 : 2000)
                                  ? 'text-red-500 font-semibold'
                                  : newMessage.length >= (selectedConversation?.channel === 'WHATSAPP' ? 3500 : 1800)
                                    ? 'text-orange-500'
                                    : 'text-gray-500'
                              }`}>
                                {newMessage.length} / {selectedConversation?.channel === 'WHATSAPP' ? 4096 : 2000}
                                {selectedConversation?.channel === 'WHATSAPP' && (
                                  <span className="ml-2 text-green-600 text-xs">
                                    <MessageCircle className="w-3 h-3 inline mr-1" />
                                    WhatsApp
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={sendMessage}
                          disabled={!newMessage.trim() || sendingMessage || uploadingFiles}
                          className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-xl transform hover:scale-105 active:scale-95 duration-200 self-end"
                        >
                          {sendingMessage || uploadingFiles ? (
                            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <Send className="w-6 h-6" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50 text-gray-300" />
                    <p className="text-lg text-gray-500">Select a conversation to start messaging</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
